import type {
    Extension,
    ExtensionAPI,
    DmApis,
    CreateExtensionArgument,
    CoreLogger,
    CreateExtArgs
} from '@wix/document-manager-core'
import type {
    Branch,
    QueryBranchesRequest,
    QueryBranchesResponse,
    CreateBranchRequest,
    CreateBranchResponse,
    Type,
    SourceType,
    UpdateBranchRequest,
    SortOrder
} from '@wix/ambassador-editor-branches-v1-branch/types'
import {ReportableError} from '@wix/document-manager-utils'
import type {DocumentServicesModelExtApi} from './documentServicesModel'
import _ from 'lodash'
import type {AppsStateEntity, GetAppsStateResponse} from '@wix/ambassador-document-v1-apps-state/types'
import type {AppDefinitionId} from '@wix/document-services-types'
import type {RMApi} from './rendererModel'

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type BranchApi = {
    getPublishedBranchId(): Promise<string | null>
    getAllBranches(): Promise<Branch[] | undefined>
    getUserAndMobileBranches(): Promise<Branch[] | undefined>
    getUserBranches(): Promise<Branch[] | undefined>
    createUserBranchFromSiteBranch(branchId: string, branchName?: string): Promise<Branch | undefined>
    createUserBranchFromTemplate(templateId: string, branchName?: string): Promise<Branch | undefined>
    deleteUserBranch(branchId: string): Promise<void>
    getBranchById(branchId: string): Promise<Branch | undefined>
    getBranchRevision(branchId: string): Promise<string | undefined>
    updateBranchName(branchId: string, branchName: string): Promise<Branch | undefined>
    getBranchAppsState(branchId: string, revision?: string): Promise<Record<string, AppsStateEntity> | undefined>
    getAppBranches(appId: string): Promise<(string | null)[]>
    getBranchId(): string
}

interface Context {
    serverFacade: any
    logger: CoreLogger
}

export interface BranchExtensionAPI extends ExtensionAPI {
    branches: BranchApi
}

const toUrl = (endpoint: string) => `/_api/editor-branches/${endpoint}`

const getBranchesApi = async (
    {serverFacade, logger}: Context,
    query: QueryBranchesRequest
): Promise<QueryBranchesResponse> => {
    logger.interactionStarted('get-branches', {extras: {queryPayload: JSON.stringify(query)}})
    const result = await serverFacade.post(toUrl('v1/branches/query'), query)
    logger.interactionEnded('get-branches')
    return result
}

const createUserBranchApi = async (
    {serverFacade, logger}: Context,
    branchPayload: Branch
): Promise<CreateBranchResponse> => {
    const payload = {
        branch: branchPayload
    } as CreateBranchRequest
    logger.interactionStarted('create-branch', {extras: {branchPayload: JSON.stringify(payload)}})
    const result = await serverFacade.post(toUrl('v1/branches'), payload)
    logger.interactionEnded('create-branch')
    return result
}

const deleteUserBranchApi = async ({serverFacade, logger}: Context, branchId: string) => {
    logger.interactionStarted('delete-branch', {extras: {branchId}})
    const result = await serverFacade.deleteJson(toUrl(`v1/branches/${branchId}`))
    logger.interactionEnded('delete-branch', {extras: {branchId}})
    return result
}

const getBranchByIdApi = async ({serverFacade, logger}: Context, branchId: string) => {
    logger.interactionStarted('get-branch', {extras: {branchId}})
    const result = await serverFacade.getJsonWithParams(toUrl(`v1/branches/${branchId}`))
    logger.interactionEnded('get-branch', {extras: {branchId}})
    return result
}

const getBranchRevisionApi = async ({serverFacade, logger}: Context, branchId: string) => {
    logger.interactionStarted('get-branch-revision', {extras: {branchId}})
    const metadata = await serverFacade.getJsonWithParams(`/_api/wix-html-editor-info-webapp/v2/site-metadata`, {
        branchId
    })
    const result = metadata.siteMetadata?.revision
    logger.interactionEnded('get-branch-revision', {extras: {branchId}})
    return result
}

const updateBranchApi = async ({serverFacade, logger}: Context, branchId: string, branchPayload: Branch) => {
    const payload = {
        branch: branchPayload
    } as UpdateBranchRequest
    logger.interactionStarted('update-branch', {extras: {branchId, branchPayload: JSON.stringify(payload)}})
    const result = await serverFacade.patchJson(toUrl(`v1/branches/${branchId}`), payload)
    logger.interactionEnded('update-branch', {extras: {branchId}})
    return result
}

const getBranchAppsStateApi = async ({serverFacade, logger}: Context, branchId: string, revision?: string) => {
    logger.interactionStarted('get-branch-apps-state', {extras: {branchId}})
    const result: Promise<GetAppsStateResponse> = await serverFacade.getJsonWithParams(
        '/document-management/v1/apps/state/get',
        {
            branchId,
            revision
        }
    )
    logger.interactionEnded('get-branch-apps-state', {extras: {branchId}})
    return result
}

const createExtension = ({environmentContext, logger}: CreateExtensionArgument): Extension => {
    const {serverFacade} = environmentContext
    const createExtensionAPI = ({extensionAPI}: CreateExtArgs): BranchExtensionAPI => {
        const sitExtensionAPI = extensionAPI as DocumentServicesModelExtApi
        const rendererModelExtAPI = extensionAPI as RMApi

        const getPublishedBranchId = async () => {
            try {
                const query: QueryBranchesRequest = {
                    query: {
                        filter: {
                            type: ['USER', 'ORIGINAL_BRANCH', 'MOBILE_NATIVE']
                        },
                        sort: [
                            {
                                fieldName: 'lastPublishDate',
                                order: 'DESC' as SortOrder.DESC
                            }
                        ],
                        cursorPaging: {
                            limit: 1
                        }
                    }
                }
                const jsonData = await getBranchesApi({serverFacade, logger}, query)
                const branchData = jsonData.branches?.[0]
                if (branchData?.lastPublishDate) {
                    return branchData.id || null
                }
                return null
            } catch (error) {
                logger.captureError(
                    new ReportableError({
                        errorType: 'GET_BRANCHES_ERROR',
                        message: 'Failed to fetch user and mobile branches',
                        extras: {
                            reason: JSON.stringify(error)
                        }
                    })
                )
                throw error
            }
        }

        const getUserAndMobileBranches = async () => {
            try {
                const query: QueryBranchesRequest = {
                    query: {
                        filter: {
                            type: ['USER', 'ORIGINAL_BRANCH', 'MOBILE_NATIVE']
                        }
                    }
                }
                const jsonData = await getBranchesApi({serverFacade, logger}, query)
                return jsonData.branches
            } catch (error) {
                logger.captureError(
                    new ReportableError({
                        errorType: 'GET_BRANCHES_ERROR',
                        message: 'Failed to fetch user and mobile branches',
                        extras: {
                            reason: JSON.stringify(error)
                        }
                    })
                )
                throw error
            }
        }

        const getAllBranches = async () => {
            try {
                const query: QueryBranchesRequest = {}
                const jsonData = await getBranchesApi({serverFacade, logger}, query)
                return jsonData.branches
            } catch (error) {
                logger.captureError(
                    new ReportableError({
                        errorType: 'GET_BRANCHES_ERROR',
                        message: 'Failed to fetch branches',
                        extras: {
                            reason: JSON.stringify(error)
                        }
                    })
                )
                throw error
            }
        }

        const getUserBranches = async () => {
            try {
                const query: QueryBranchesRequest = {
                    query: {
                        filter: {
                            type: ['USER', 'ORIGINAL_BRANCH']
                        }
                    }
                }
                const jsonData = await getBranchesApi({serverFacade, logger}, query)
                return jsonData.branches
            } catch (error) {
                logger.captureError(
                    new ReportableError({
                        errorType: 'GET_USER_BRANCHES_ERROR',
                        message: 'Failed to fetch user branches',
                        extras: {
                            reason: JSON.stringify(error)
                        }
                    })
                )
                throw error
            }
        }

        const createUserBranchFromSiteBranch = async (branchId: string, branchName?: string) => {
            try {
                const branchPayload: Branch = {
                    type: 'USER' as Type.USER,
                    sourceType: 'SOURCE_BRANCH' as SourceType.SOURCE_BRANCH,
                    sourceBranchProperties: {
                        branchId
                    },
                    ...(branchName && {name: branchName})
                }
                const jsonData = await createUserBranchApi({serverFacade, logger}, branchPayload)
                return jsonData.branch
            } catch (error) {
                logger.captureError(
                    new ReportableError({
                        errorType: 'CREATE_BRANCH_ERROR',
                        message: 'Failed to create branch',
                        extras: {
                            sourceBranchId: branchId,
                            reason: JSON.stringify(error)
                        }
                    })
                )
                throw error
            }
        }

        const createUserBranchFromTemplate = async (templateId: string, branchName?: string) => {
            try {
                const branchPayload: Branch = {
                    type: 'USER' as Type.USER,
                    sourceType: 'SOURCE_TEMPLATE' as SourceType.SOURCE_TEMPLATE,
                    sourceTemplateProperties: {
                        templateId
                    },
                    ...(branchName && {name: branchName})
                }
                const jsonData = await createUserBranchApi({serverFacade, logger}, branchPayload)
                return jsonData.branch
            } catch (error) {
                logger.captureError(
                    new ReportableError({
                        errorType: 'CREATE_BRANCH_ERROR',
                        message: 'Failed to create branch',
                        extras: {
                            sourceTemplateId: templateId,
                            reason: JSON.stringify(error)
                        }
                    })
                )
                throw error
            }
        }

        const getBranchById = async (branchId: string) => {
            try {
                const branchData = await getBranchByIdApi({serverFacade, logger}, branchId)
                return branchData.branch
            } catch (error) {
                logger.captureError(
                    new ReportableError({
                        errorType: 'FETCH_BRANCH_ERROR',
                        message: 'Failed to fetch branch',
                        extras: {
                            branchId,
                            reason: JSON.stringify(error)
                        }
                    })
                )
                throw error
            }
        }

        const getBranchRevision = async (branchId: string) => {
            try {
                return await getBranchRevisionApi({serverFacade, logger}, branchId)
            } catch (error) {
                logger.captureError(
                    new ReportableError({
                        errorType: 'FETCH_BRANCH_REVISION_ERROR',
                        message: 'Failed to fetch branch revision',
                        extras: {
                            branchId,
                            reason: JSON.stringify(error)
                        }
                    })
                )
                throw error
            }
        }

        const updateBranchName = async (branchId: string, branchName: string) => {
            try {
                //Get branch internal revision
                const branchData = await getBranchByIdApi({serverFacade, logger}, branchId)
                if (_.isEmpty(branchData)) {
                    throw new Error('Branch was not found')
                }
                const branchPayload: Branch = {
                    id: branchId,
                    name: branchName,
                    revision: branchData.branch.revision
                }
                const jsonData = await updateBranchApi({serverFacade, logger}, branchId, branchPayload)
                return jsonData.branch
            } catch (error) {
                logger.captureError(
                    new ReportableError({
                        errorType: 'UPDATE_BRANCH_ERROR',
                        message: 'Failed to update branch',
                        extras: {
                            branchId,
                            reason: JSON.stringify(error)
                        }
                    })
                )
                throw error
            }
        }

        const getBranchAppsState = async (branchId: string, revision?: string) => {
            try {
                const jsonData = await getBranchAppsStateApi({serverFacade, logger}, branchId, revision)
                return jsonData.appsState
            } catch (error) {
                logger.captureError(
                    new ReportableError({
                        errorType: 'GET_BRANCH_APP_STATE_ERROR',
                        message: 'Failed to fetch branch apps state',
                        extras: {
                            reason: JSON.stringify(error)
                        }
                    })
                )
                throw error
            }
        }

        const getAllBranchesWithApps = async (appDefIds: string[]): Promise<Record<AppDefinitionId, string[]>> => {
            logger.interactionStarted('getAllBranchesWithApps', {extras: {appDefIds}})
            if (appDefIds.length === 0) {
                throw new ReportableError({errorType: 'MissingAppDefinitionId', message: 'missing appDefinitionId'})
            }
            try {
                const allUserBranches = await getUserAndMobileBranches()
                if (!allUserBranches) {
                    return {}
                }
                const states = await Promise.all(
                    allUserBranches.map(async (branch: Branch) => {
                        if (branch.id) {
                            return await getBranchAppsState(branch.id)
                        }
                        return null
                    })
                )

                const branchesWithApps = appDefIds.reduce((branchesIncludingApp, appDefId) => {
                    branchesIncludingApp[appDefId] = []
                    states.forEach((state, index) => {
                        const branchId = allUserBranches[index].id
                        if (branchId && state?.[appDefId]?.status === 'INSTALLED') {
                            branchesIncludingApp[appDefId].push(branchId)
                        }
                    })
                    return branchesIncludingApp
                }, {} as Record<AppDefinitionId, string[]>)
                logger.interactionEnded('getAllBranchesWithApps', {extras: {branchesWithApps, appDefIds}})
                return branchesWithApps
            } catch (error) {
                logger.captureError(
                    new ReportableError({
                        errorType: 'GET_BRANCH_APP_STATE_ERROR',
                        message: 'Failed to fetch branch apps state',
                        extras: {
                            reason: JSON.stringify(error)
                        }
                    })
                )
                throw error
            }
        }

        const getBranchId = () => {
            return sitExtensionAPI.siteAPI.getBranchId() ?? '00000000-0000-0000-0000-000000000000'
        }

        const deleteUserBranch = async (branchId: string) => {
            try {
                const branchData = await getBranchByIdApi({serverFacade, logger}, branchId)
                if (_.isEmpty(branchData)) {
                    throw new Error('Branch was not found')
                }
                if (branchData.branch.type === 'ORIGINAL_BRANCH') {
                    throw new Error('Main branch cannot be deleted')
                }
                const currentBranchId = sitExtensionAPI.siteAPI.getBranchId()
                if (currentBranchId === branchId) {
                    throw new Error('Current branch cannot be deleted')
                }
                const branchAppState = await getBranchAppsState(branchId)
                await deleteUserBranchApi({serverFacade, logger}, branchId)
                if (branchAppState) {
                    const appsInstalledOnDeletedBranch = Object.keys(branchAppState).filter(
                        appDefId => branchAppState[appDefId].status === 'INSTALLED'
                    )
                    if (appsInstalledOnDeletedBranch.length > 0) {
                        const appsOnBranches = await getAllBranchesWithApps(appsInstalledOnDeletedBranch)
                        const nonInstalledApps = Object.keys(appsOnBranches).reduce(
                            (notInstalledOnAnyBranch, appDefId) => {
                                const branches = appsOnBranches[appDefId]
                                if (branches.length === 0) {
                                    notInstalledOnAnyBranch.push(appDefId)
                                }
                                return notInstalledOnAnyBranch
                            },
                            [] as AppDefinitionId[]
                        )
                        if (!_.isEmpty(nonInstalledApps)) {
                            rendererModelExtAPI.rendererModel.modifySiteApps(
                                {appsToRevoke: nonInstalledApps},
                                getBranchId()
                            )
                        }
                    }
                }
            } catch (error) {
                logger.captureError(
                    new ReportableError({
                        errorType: 'DELETE_BRANCH_ERROR',
                        message: 'Failed to delete branch',
                        extras: {
                            branchId,
                            reason: JSON.stringify(error)
                        }
                    })
                )
                throw error
            }
        }
        return {
            branches: {
                getPublishedBranchId,
                getUserAndMobileBranches,
                getAllBranches,
                getUserBranches,
                createUserBranchFromSiteBranch,
                createUserBranchFromTemplate,
                deleteUserBranch: async (branchId: string) => deleteUserBranch(branchId),
                getBranchById,
                getBranchRevision,
                updateBranchName,
                getBranchAppsState,
                getAppBranches: async (appDefId: AppDefinitionId) => {
                    if (!appDefId) {
                        throw new ReportableError({
                            errorType: 'MissingAppDefinitionId',
                            message: 'missing appDefinitionId'
                        })
                    }
                    return (await getAllBranchesWithApps([appDefId]))[appDefId]
                },
                getBranchId
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {branches} = extensionAPI as BranchExtensionAPI
        return {
            branches: {
                getPublishedBranchId: branches.getPublishedBranchId,
                getUserAndMobileBranches: branches.getUserAndMobileBranches,
                getAllBranches: branches.getAllBranches,
                getUserBranches: branches.getUserBranches,
                createUserBranchFromSiteBranch: branches.createUserBranchFromSiteBranch,
                createUserBranchFromTemplate: branches.createUserBranchFromTemplate,
                deleteUserBranch: branches.deleteUserBranch,
                getBranchById: branches.getBranchById,
                getBranchRevision: branches.getBranchRevision,
                updateBranchName: branches.updateBranchName,
                getBranchAppsState: branches.getBranchAppsState,
                getAppBranches: branches.getAppBranches
            }
        }
    }

    return {
        name: 'branches',
        createExtensionAPI,
        createPublicAPI
    }
}

export {createExtension}
